import { AGENT_URL } from '@/config/env'
import type { DutyBranch } from '@/features/duties/branches'
import type {
  DutyMcpConnectionItem,
  DutyToolListItem,
  DutyToolOverrideWrite,
} from '@/features/tools/types'
import { fetchJsonWithAuth, mutateJsonWithAuth } from '@/lib/api'

export interface BenefitsCalQuestionnaireSchemaSection {
  key: string
  title: string | null
  schema: Record<string, unknown>
}

function dutyMcpConnectionsUrl(
  dutyId: string,
  connectionId: string | undefined,
  branch: DutyBranch | null
): string {
  const base = `${AGENT_URL}/duties/${dutyId}/mcp-connections`
  const path = connectionId ? `${base}/${connectionId}` : base
  return branch === null ? path : `${path}?branch=${encodeURIComponent(branch)}`
}

export async function fetchDutyMcpConnections(
  token: string,
  dutyId: string,
  signal?: AbortSignal,
  branch: DutyBranch | null = null
): Promise<DutyMcpConnectionItem[]> {
  const data = await fetchJsonWithAuth<{
    connections: DutyMcpConnectionItem[]
  }>({
    token,
    url: dutyMcpConnectionsUrl(dutyId, undefined, branch),
    signal,
    errorMessage: 'Failed to fetch duty MCP connections',
  })
  return data.connections
}

export async function includeDutyMcpConnection(
  token: string,
  dutyId: string,
  connectionId: string,
  branch: DutyBranch | null = null
): Promise<void> {
  await mutateJsonWithAuth({
    token,
    url: dutyMcpConnectionsUrl(dutyId, connectionId, branch),
    method: 'POST',
    errorMessage: 'Failed to enable MCP connection for this duty',
  })
}

export async function excludeDutyMcpConnection(
  token: string,
  dutyId: string,
  connectionId: string,
  branch: DutyBranch | null = null
): Promise<void> {
  await mutateJsonWithAuth({
    token,
    url: dutyMcpConnectionsUrl(dutyId, connectionId, branch),
    method: 'DELETE',
    errorMessage: 'Failed to disable MCP connection for this duty',
  })
}

export interface BenefitsCalQuestionnaireSchemaBundle {
  version: string
  digest: string
  sections: BenefitsCalQuestionnaireSchemaSection[]
}

export async function fetchBenefitsCalQuestionnaireSchema(
  token: string,
  signal?: AbortSignal
): Promise<BenefitsCalQuestionnaireSchemaBundle> {
  return fetchJsonWithAuth<BenefitsCalQuestionnaireSchemaBundle>({
    token,
    url: `${AGENT_URL}/benefitscal-questionnaires/schema`,
    signal,
    errorMessage: 'Failed to fetch BenefitsCal questionnaire schema',
  })
}

function dutyToolsUrl(
  dutyId: string,
  toolId: string | null,
  branch: DutyBranch | null
): string {
  const path = toolId
    ? `${AGENT_URL}/duties/${dutyId}/tools/${toolId}`
    : `${AGENT_URL}/duties/${dutyId}/tools`
  return branch === null ? path : `${path}?branch=${encodeURIComponent(branch)}`
}

export async function fetchDutyTools(
  token: string,
  dutyId: string,
  branch: DutyBranch | null = null,
  signal?: AbortSignal
): Promise<DutyToolListItem[]> {
  const data = await fetchJsonWithAuth<{ tools: DutyToolListItem[] }>({
    token,
    url: dutyToolsUrl(dutyId, null, branch),
    signal,
    errorMessage: 'Failed to fetch duty tools',
  })
  return data.tools
}

export async function includeDutyTool(
  token: string,
  dutyId: string,
  toolId: string,
  overrides?: DutyToolOverrideWrite,
  branch: DutyBranch | null = null
): Promise<DutyToolListItem> {
  const data = await mutateJsonWithAuth<{ tool: DutyToolListItem }>({
    token,
    url: dutyToolsUrl(dutyId, toolId, branch),
    method: 'POST',
    body: overrides,
    errorMessage: 'Failed to include tool for this duty',
  })
  return data.tool
}

export async function patchDutyToolOverrides(
  token: string,
  dutyId: string,
  toolId: string,
  overrides: DutyToolOverrideWrite,
  branch: DutyBranch | null = null
): Promise<DutyToolListItem> {
  const data = await mutateJsonWithAuth<{ tool: DutyToolListItem }>({
    token,
    url: dutyToolsUrl(dutyId, toolId, branch),
    method: 'PATCH',
    body: overrides,
    errorMessage: 'Failed to update tool overrides',
  })
  return data.tool
}

export async function excludeDutyTool(
  token: string,
  dutyId: string,
  toolId: string,
  branch: DutyBranch | null = null
): Promise<void> {
  await mutateJsonWithAuth<void>({
    token,
    url: dutyToolsUrl(dutyId, toolId, branch),
    method: 'DELETE',
    errorMessage: 'Failed to remove tool from this duty',
  })
}
